import { useState } from 'react'
import { useCreateRequest } from '../lib/createRequest'
import { Link } from 'react-router-dom'
import { useCustomers, useDevices } from '../api/queries'
import type { Device } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { DeviceForm } from '../components/DeviceForm'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDate } from '../lib/format'

/**
 * Enhedslisten: alt hvad der står ude hos kunderne og skal serviceres.
 * Enheder der snart forfalder, står øverst - det er dem, der skal planlægges.
 */
export function DevicesPage() {
  const { isProjectManager } = useAuth()
  const [search, setSearch] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [onServicePlan, setOnServicePlan] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [showForm, setShowForm] = useState(false)
  useCreateRequest(['enhed'], () => {
    if (isProjectManager) setShowForm(true)
  })

  const { data: customers } = useCustomers()
  const { data, isLoading, error } = useDevices({
    search: search || undefined,
    customerId: customerId ? Number(customerId) : undefined,
    onServicePlan: onServicePlan || undefined,
    includeInactive: includeInactive || undefined
  })

  return (
    <>
      <div className="row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={{ maxWidth: 240 }}
          placeholder="Søg i navn, kode, kunde eller adresse ..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select style={{ maxWidth: 190 }} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">Alle kunder</option>
          {customers?.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>

        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={onServicePlan}
            onChange={(event) => setOnServicePlan(event.target.checked)}
          />
          Kun serviceaftaler
        </label>
        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Vis udtjente
        </label>

        {isProjectManager && (
          <button
            className="btn btn--primary"
            style={{ marginLeft: 'auto' }}
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? 'Luk' : 'Ny enhed'}
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ marginBottom: 18 }}>
          <DeviceForm device={null} onClose={() => setShowForm(false)} onSaved={() => setShowForm(false)} />
        </div>
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.length === 0 && (
        <EmptyState>
          Der er ingen enheder endnu. Enheder oprettes her eller automatisk, når en sag afsluttes.
        </EmptyState>
      )}

      {/* Søger man på en kunde eller adresse, står enhederne samlet pr. adresse (beslutning 109). */}
      {data && data.length > 0 && (search.trim() || customerId) && <DevicesByAddress devices={data} />}

      {data && data.length > 0 && !search.trim() && !customerId && (
        <Card>
          <div className="stack">
            {data.map((device) => (
              <DeviceRow key={device.id} device={device} />
            ))}
          </div>
        </Card>
      )}
    </>
  )
}

/** Adressen som én linje: "Pletvej 19, 7620 Lemvig". */
export function deviceAddressLine(device: Device) {
  const place = [device.postalCode, device.city].filter(Boolean).join(' ')
  return [device.address, place].filter(Boolean).join(', ')
}

/**
 * Enhederne samlet pr. adresse, hvor de står (beslutning 109) - så man kan slå en kunde eller en adresse op og se,
 * hvad der er der. Bruges på Enheder-siden og på kundens side.
 */
export function DevicesByAddress({ devices }: { devices: Device[] }) {
  const groups = new Map<string, Device[]>()
  for (const device of devices) {
    const key = deviceAddressLine(device) || 'Uden adresse'
    groups.set(key, [...(groups.get(key) ?? []), device])
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      {[...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b, 'da'))
        .map(([address, list]) => (
          <Card
            key={address}
            title={`📍 ${address}`}
            actions={<span className="tiny muted">{list.length} {list.length === 1 ? 'enhed' : 'enheder'}</span>}
          >
            <div className="stack">
              {list.map((device) => (
                <DeviceRow key={device.id} device={device} />
              ))}
            </div>
          </Card>
        ))}
    </div>
  )
}

/** Én linje i enhedslisten. Forfaldsdatoen er det vigtigste, så den står tydeligt. */
function DeviceRow({ device }: { device: Device }) {
  const due = serviceStatus(device)

  return (
    <Link className="row row--between device-row" to={`/devices/${device.id}`}>
      <div style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 8 }}>
          <strong className="small">{device.name}</strong>
          <span className="tiny muted device-row__code">{device.code}</span>
          {!device.isActive && (
            <span className="pill" style={{ background: '#F1F5F9', color: '#64748B' }}>
              Udtjent
            </span>
          )}
        </div>
        <div className="tiny muted">
          {[device.customerName, device.location, device.deviceType].filter(Boolean).join(' · ')}
        </div>
      </div>

      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {due ? (
          <>
            <span className="pill" style={{ background: due.background, color: due.color }}>
              {due.label}
            </span>
            <div className="tiny muted">{formatDate(device.nextServiceDate)}</div>
          </>
        ) : (
          <span className="tiny muted">Ingen serviceaftale</span>
        )}
      </div>
    </Link>
  )
}

/**
 * Hvor presserende er servicen? Farverne følger samme logik som deadlines
 * på sagerne: rød når det er overskredet, gul når det er lige om lidt.
 */
export function serviceStatus(device: Device) {
  if (!device.nextServiceDate || !device.serviceIntervalMonths) {
    return null
  }

  const due = new Date(device.nextServiceDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000)

  if (days < 0) {
    return { label: `Overskredet ${-days} dage`, background: '#FEE2E2', color: '#991B1B', days }
  }
  if (days === 0) {
    return { label: 'Forfalder i dag', background: '#FEE2E2', color: '#991B1B', days }
  }
  if (days <= 30) {
    return { label: `Om ${days} dage`, background: '#FEF3C7', color: '#92400E', days }
  }

  return { label: `Om ${Math.round(days / 30)} mdr.`, background: '#F1F5F9', color: '#334155', days }
}
